import { useState, useMemo } from 'react';
import { analyzeAgency } from '../services/api';

const STEPS = [
  // 1. Revenue
  { key: 'grossRevenue',   label: 'Gross annual revenue',                                                       type: 'currency' },
  // 2. Direct costs — salaries AND expenses that only exist when you have a paying customer
  { key: 'directCosts',    label: 'Direct contract-related salaries & expenses',                                 type: 'currency' },
  // 3. Indirect / overhead — salaries and expenses regardless of whether there are any contracts
  { key: 'indirectCosts',  label: 'Indirect expenses & salaries (overhead)',                                     type: 'currency' },
];

const INITIAL_VALUES = Object.fromEntries(STEPS.map(s => [s.key, '']));

// ─── Currency constants ──────────────────────────────────────────────────────

const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'];

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  CAD: 'CA$',
  AUD: 'AU$',
};

const CURRENCY_FLAGS = {
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  INR: '🇮🇳',
  CAD: '🇨🇦',
  AUD: '🇦🇺',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function validateValue(val, type) {
  if (val === '' || val === '.') return 'This field is required.';
  if (type === 'integer') {
    const n = parseInt(val, 10);
    if (isNaN(n) || String(n) !== String(val).trim()) return 'Please enter a whole number.';
    if (n < 0) return 'Value cannot be negative.';
    return null;
  }
  if (type === 'percent') {
    const n = parseFloat(val);
    if (isNaN(n)) return 'Please enter a valid number.';
    if (n < 0 || n > 100) return 'Must be between 0 and 100.';
    return null;
  }
  const num = parseFloat(val);
  if (isNaN(num)) return 'Please enter a valid number.';
  if (num < 0) return 'Value cannot be negative.';
  return null;
}

function toPayload(values, selectedCurrency) {
  // The free 3-question form collects combined totals.
  // The backend expects the original split fields, so we send the combined
  // value into one field and 0 into the other — the engine adds them anyway.
  return {
    gross_annual_revenue:     values.grossRevenue,
    direct_contract_salaries: values.directCosts,
    direct_contract_expenses: '0',
    overhead_expenses:        values.indirectCosts,
    non_billable_salaries:    '0',
    currency:                 selectedCurrency,
  };
}

// ─── Live EBITDA preview helper ──────────────────────────────────────────────

function computeLiveEbitda(values) {
  const r = parseFloat(values.grossRevenue);
  if (isNaN(r) || r === 0) return null;
  const d = parseFloat(values.directCosts)   || 0;
  const i = parseFloat(values.indirectCosts) || 0;
  return r - d - i;
}

// ─── Component ───────────────────────────────────────────────────────────────

function ValuationForm({ onResult, currency, onCurrencyChange }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [values, setValues] = useState(INITIAL_VALUES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const step = STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS.length - 1;
  const allValid = STEPS.every(s => validateValue(values[s.key], s.type) === null);

  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const liveEbitda = useMemo(() => computeLiveEbitda(values), [values]);

  function handleChange(e) {
    const val = e.target.value;
    const pattern = step.type === 'integer' ? /^\d*$/ : /^\d*\.?\d*$/;
    if (val === '' || pattern.test(val)) {
      setValues(prev => ({ ...prev, [step.key]: val }));
      setError(null);
    }
  }

  function handleNext() {
    const err = validateValue(values[step.key], step.type);
    if (err) { setError(err); return; }
    setCurrentStep(prev => prev + 1);
  }

  function handleBack() {
    setError(null);
    setCurrentStep(prev => prev - 1);
  }

  async function handleSubmit() {
    const err = validateValue(values[step.key], step.type);
    if (err) { setError(err); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await analyzeAgency(toPayload(values, currency));
      onResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Progress: based on current step
  const progressPct = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="form-slide">

      {/* ── Progress indicator ── */}
      <div className="form-progress-row">
        <div className="form-progress-track">
          <div
            className="form-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="form-step-label">
          Step {currentStep + 1} of {STEPS.length}
        </span>
      </div>

      <>
          {/* ── Currency selector — persistent across all steps ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <select
              value={currency}
              onChange={e => onCurrencyChange(e.target.value)}
              style={{
                fontSize: 13,
                padding: '4px 10px',
                borderRadius: 9999,
                border: '1px solid #D1D5DB',
                background: '#F9FAFB',
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              {SUPPORTED_CURRENCIES.map(code => (
                <option key={code} value={code}>
                  {CURRENCY_FLAGS[code]} {code}
                </option>
              ))}
            </select>
          </div>

          {/* ── Current step ── */}
          <label className="form-label">{step.label}</label>
          <div className="input-wrapper">
            {step.type === 'currency' && (
              <span className="input-prefix">{symbol}</span>
            )}
            <input
              className="form-input"
              type="text"
              inputMode={step.type === 'integer' ? 'numeric' : 'decimal'}
              value={values[step.key]}
              onChange={handleChange}
              /* Wider left-padding for multi-char symbols like CA$ and AU$ */
              style={
                step.type === 'currency' && symbol.length > 1
                  ? { paddingLeft: 40 }
                  : undefined
              }
            />
            {step.type === 'percent' && <span className="input-prefix">%</span>}
          </div>
          {error && <p className="form-error">{error}</p>}

          {/* ── Live EBITDA preview ── */}
          {liveEbitda !== null && (
            <div className="ebitda-preview">
              <div className="ebitda-preview-head">
                <div className="ebitda-preview-copy">
                  <span className="ebitda-preview-label">Live EBITDA estimate</span>
                  <span className="ebitda-preview-hint">Updates as you fill in each field</span>
                </div>
                <span className="ebitda-preview-badge">Preview</span>
              </div>
              <div className="ebitda-preview-main">
                <span className={`ebitda-preview-value${liveEbitda < 0 ? ' negative' : ''}`}>
                {liveEbitda < 0 ? '−' : ''}{symbol}{Math.abs(liveEbitda).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </span>
                <span className="ebitda-preview-formula">Revenue âˆ’ direct costs âˆ’ overhead</span>
              </div>
            </div>
          )}

          {/* ── Navigation ── */}
          <div className="form-nav">
            {!isFirst && (
              <button className="btn btn-outline" onClick={handleBack} disabled={loading}>Back</button>
            )}
            {!isLast && (
              <button className="btn" onClick={handleNext}>Next</button>
            )}
            {isLast && (
              <button className="btn" onClick={handleSubmit} disabled={!allValid || loading}>
                {loading ? 'Calculating...' : 'Confirm & Submit'}
              </button>
            )}
          </div>
        </>
    </div>
  );
}

export default ValuationForm;
